import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSiteContext } from '../../context/SiteContext';
import ReferenceCard from '../ui/ReferenceCard';
import SafeIcon from '../../common/SafeIcon';
import activityTracker from '../../lib/activityTracker';

const FILTERS = [
  'All', 'Trades', 'Local Services', 'Professional Services', 'Booking-Based', 'Lead Generation', 'Product Showcase', 'Automation Systems', 'Premium Visual',
];

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    return value.split(',').map(s => s.trim()).filter(Boolean);
  }
  return [];
};

const normalize = (value) => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
const editDistanceWithinOne = (a, b) => {
  if (Math.abs(a.length - b.length) > 1 || !a || !b) return false;
  let i = 0, j = 0, errors = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) { i++; j++; continue; }
    if (++errors > 1) return false;
    if (a.length > b.length) i++;
    else if (b.length > a.length) j++;
    else { i++; j++; }
  }
  return errors + Number(i < a.length || j < b.length) <= 1;
};
const calculateSearchRank = (ref, query) => {
  const q = normalize(query);
  const tags = toArray(ref.tags);
  const searchKeywords = toArray(ref.searchKeywords);
  const suburbs = toArray(ref.suburbs);
  const searchableText = `${ref.publicTitle || ''} ${ref.websiteName || ''} ${ref.businessType || ''} ${ref.primaryCategory || ''} ${ref.outcomeType || ''} ${ref.location || ''} ${ref.state || ''} ${ref.region || ''} ${tags.join(' ')} ${searchKeywords.join(' ')} ${suburbs.join(' ')} ${ref.shortDescription || ''} ${ref.longDescription || ''}`.toLowerCase();
  const title = normalize(ref.publicTitle);
  const type = normalize(ref.businessType);
  if (title === q) return 100;
  if (type === q) return 90;
  if (tags.some(t => t.toLowerCase() === q)) return 85;
  if (title.startsWith(q)) return 70;
  if (type.startsWith(q)) return 65;
  if (tags.some(t => t.toLowerCase().startsWith(q))) return 60;
  if (searchableText.includes(q)) return 50;
  const terms = normalize(searchableText).split(/\s+/);
  if (q.length >= 4 && terms.some(term => editDistanceWithinOne(term, q))) return 30;
  return 0;
};

const ReferenceGrid = () => {
  const { data } = useSiteContext();
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(12);

  const handleFilterChange = useCallback((filter) => {
    setActiveFilter(filter);
    setVisibleCount(12);
    if (filter !== 'All') {
      activityTracker.trackFilter(filter);
    }
  }, []);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setVisibleCount(12);
    if (value.trim()) {
      activityTracker.trackSearch(value);
    }
  }, []);

  const filteredReferences = useMemo(() => {
    let refs = data.websiteReferences.filter((r) => r.active);
    if (activeFilter !== 'All') {
      refs = refs.filter((r) => {
        const tags = toArray(r.tags);
        return (
          normalize(r.businessType) === normalize(activeFilter) ||
          normalize(r.primaryCategory) === normalize(activeFilter) ||
          normalize(r.outcomeType) === normalize(activeFilter) ||
          tags.some(tag => normalize(tag) === normalize(activeFilter))
        );
      });
    }
    if (searchQuery.trim()) {
      const q = normalize(searchQuery);
      refs = refs
        .map((ref) => ({ ...ref, searchRank: calculateSearchRank(ref, q) }))
        .filter((ref) => ref.searchRank > 0)
        .sort((a, b) => {
          if (b.searchRank !== a.searchRank) return b.searchRank - a.searchRank;
          return a.displayPriority - b.displayPriority;
        });
    } else {
      refs = refs.sort((a, b) => a.displayPriority - b.displayPriority);
    }
    return refs;
  }, [data.websiteReferences, activeFilter, searchQuery]);

  const displayedRefs = filteredReferences.slice(0, visibleCount);
  const hasMore = visibleCount < filteredReferences.length;

  return (
    <section id="directory" className="py-20 bg-black/20 border-y border-white/5">
      <div className="w-full max-w-[1440px] 2xl:max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-8 2xl:px-10">
        <div className="flex flex-col lg:flex-row justify-between items-center gap-6 mb-12">
          <h2 className="text-3xl font-bold text-white w-full lg:w-auto">Website Directory</h2>
          <div className="w-full lg:w-96 relative">
            <label htmlFor="search-input" className="sr-only">Search website references</label>
            <SafeIcon name="Search" className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              id="search-input"
              type="text"
              placeholder="Search by industry, service, location..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full pl-11 pr-4 py-3 bg-[#13131a] border border-white/10 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
            />
          </div>
        </div>
        <div className="flex overflow-x-auto hide-scrollbar sm:flex-wrap gap-2 mb-10 pb-2">
          {FILTERS.map((filter) => (
            <button
              key={filter}
              onClick={() => handleFilterChange(filter)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                activeFilter === filter
                  ? 'bg-blue-600 text-white shadow-[0_0_15px_rgba(37,99,235,0.4)] border border-blue-500'
                  : 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white border border-transparent'
              }`}
            >
              {filter}
            </button>
          ))}
        </div>
        {displayedRefs.length > 0 ? (
          <motion.div className="reference-card-grid">
            <AnimatePresence initial={false}>
              {displayedRefs.map((reference) => (
                <ReferenceCard key={reference.websiteId} reference={reference} />
              ))}
            </AnimatePresence>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="py-20 text-center text-slate-400"
          >
            <SafeIcon name="Inbox" className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg">No website references match your search.</p>
            <button
              onClick={() => { setSearchQuery(''); setActiveFilter('All'); }}
              className="mt-4 text-blue-400 hover:text-blue-300 font-medium"
            >
              Clear filters
            </button>
          </motion.div>
        )}
        {hasMore && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="mt-16 flex items-center justify-center"
          >
            <motion.button
              onClick={() => setVisibleCount((prev) => Math.min(prev + 10, filteredReferences.length))}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="load-more-neon-button group relative isolate overflow-hidden rounded-full bg-[#05060a] px-10 py-4 font-semibold text-base text-white border border-blue-400/30 shadow-[0_14px_45px_rgba(0,0,0,0.45)] focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-[#0a0a0e]"
            >
              <span className="relative z-[3] flex items-center gap-2.5">
                Load More References
                <SafeIcon name="ChevronDown" className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
              </span>
            </motion.button>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default ReferenceGrid;